import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesCompanyApi, companyFeaturesApi, type Hedge, type HedgeDashboard } from "@/services/api";
import {
  Shield,
  Search,
  MoreHorizontal,
  Eye,
  Loader2,
  RefreshCw,
  Plus,
  DollarSign,
  TrendingUp,
  Calendar,
  AlertTriangle,
  Clock,
  Wallet,
  Calculator,
} from "lucide-react";
import { formatDate, cn } from "@/lib/utils";
import { HedgeSimulatorModal } from "@/components/company/HedgeSimulatorModal";

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" }
> = {
  PENDING_GUARANTEE: { label: "Aguardando Garantia", variant: "warning" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning" },
  ACTIVE: { label: "Proteção Ativa", variant: "success" },
  PENDING_SETTLEMENT: { label: "Aguardando Liquidação", variant: "warning" },
  SETTLED: { label: "Liquidado", variant: "secondary" },
  REJECTED: { label: "Rejeitado", variant: "destructive" },
  DEFAULTED: { label: "Inadimplente", variant: "destructive" },
  CANCELLED: { label: "Cancelado", variant: "secondary" },
};

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatBrl(value: string | number | undefined): string {
  if (!value) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(num);
}

export function ProtecaoCambialPage() {
  const navigate = useNavigate();
  const [hedges, setHedges] = useState<Hedge[]>([]);
  const [dashboard, setDashboard] = useState<HedgeDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showSimulator, setShowSimulator] = useState(false);
  const limit = 10;

  // Verificar se empresa tem acesso à feature
  const { data: enabledFeatures, isLoading: loadingFeatures } = useQuery({
    queryKey: ["company-features"],
    queryFn: () => companyFeaturesApi.getEnabled(),
    staleTime: 5 * 60 * 1000,
  });

  const hasAccess = enabledFeatures?.includes("IMPORT_HEDGE");

  // Redirecionar se não tem acesso
  useEffect(() => {
    if (!loadingFeatures && enabledFeatures && !hasAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [loadingFeatures, enabledFeatures, hasAccess, navigate]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const hedgesData = await hedgesCompanyApi.listHedges({
        page,
        limit,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      setHedges(hedgesData.items);
      setTotal(hedgesData.total);

      // Calcular métricas do dashboard
      const activeHedges = hedgesData.items.filter(h => h.status === "ACTIVE");
      const pendingHedges = hedgesData.items.filter(h =>
        h.status === "PENDING_GUARANTEE" || h.status === "PENDING_APPROVAL"
      );

      // Próximo vencimento
      const nextDueDate = hedgesData.items
        .filter(h => h.status === "ACTIVE" || h.status === "PENDING_SETTLEMENT")
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0]?.dueDate;

      setDashboard({
        activeHedgesCount: activeHedges.length,
        totalVolumeUsdc: activeHedges.reduce((sum, h) => sum + parseFloat(h.hedgeAmountUsdc || "0"), 0).toString(),
        totalGuaranteesUsdc: activeHedges.reduce((sum, h) => sum + parseFloat(h.guaranteeAmountUsdc || "0"), 0).toString(),
        realizedRevenueBrl: "0",
        projectedRevenueBrl: "0",
        hedgesAtRiskCount: 0,
        pendingApprovalCount: pendingHedges.length,
        nextDueDate,
      });
    } catch (error) {
      console.error("Erro ao carregar hedges:", error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtro local por busca
  const filteredHedges = hedges.filter((hedge) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      hedge.hedgeCode?.toLowerCase().includes(searchLower) ||
      hedge.invoice?.invoiceNumber?.toLowerCase().includes(searchLower) ||
      hedge.invoice?.supplierName?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages = Math.ceil(total / limit);

  // Mostrar loading enquanto verifica acesso
  if (loadingFeatures) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  // Se não tem acesso, não renderiza (useEffect redireciona)
  if (!hasAccess) {
    return null;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Proteção Cambial"
        description="Gerencie suas proteções cambiais para importações"
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowSimulator(true)}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Simular
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/protecao-cambial/novo")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Proteção
            </Button>
          </div>
        }
      />

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Proteções Ativas
            </CardTitle>
            <Shield className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{dashboard?.activeHedgesCount || 0}</p>
            <p className="text-xs text-muted-foreground">proteções ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Volume Protegido
            </CardTitle>
            <DollarSign className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatUsdc(dashboard?.totalVolumeUsdc)}
            </p>
            <p className="text-xs text-muted-foreground">em proteções ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Garantias Depositadas
            </CardTitle>
            <Wallet className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatUsdc(dashboard?.totalGuaranteesUsdc)}
            </p>
            <p className="text-xs text-muted-foreground">USDC reservado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Próximo Vencimento
            </CardTitle>
            <Calendar className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {dashboard?.nextDueDate ? formatDate(dashboard.nextDueDate) : "-"}
            </p>
            <p className="text-xs text-muted-foreground">
              {dashboard?.pendingApprovalCount
                ? `${dashboard.pendingApprovalCount} pendente${dashboard.pendingApprovalCount > 1 ? 's' : ''}`
                : "nenhum pendente"
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[250px]">
          <label className="text-sm font-medium mb-2 block">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Código, invoice ou fornecedor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredHedges.length === 0 ? (
            <div className="p-8 text-center">
              <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {search || statusFilter !== "all"
                  ? "Nenhum hedge encontrado com os filtros aplicados"
                  : "Você ainda não tem proteções cambiais"}
              </p>
              {!search && statusFilter === "all" && (
                <Button onClick={() => navigate("/protecao-cambial/novo")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Proteção
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Código</th>
                    <th className="p-4 font-medium hidden md:table-cell">Invoice / Fornecedor</th>
                    <th className="p-4 font-medium">Valor USDC</th>
                    <th className="p-4 font-medium hidden sm:table-cell">Taxa Travada</th>
                    <th className="p-4 font-medium hidden lg:table-cell">Valor BRL</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium hidden sm:table-cell">Vencimento</th>
                    <th className="p-4 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHedges.map((hedge) => {
                    const status = statusConfig[hedge.status] || {
                      label: hedge.status,
                      variant: "secondary" as const,
                    };
                    const daysUntilDue = Math.ceil(
                      (new Date(hedge.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                    );
                    const isNearDue = daysUntilDue <= 7 && daysUntilDue > 0;
                    const isOverdue = daysUntilDue < 0;

                    return (
                      <tr
                        key={hedge.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer"
                        onClick={() => navigate(`/protecao-cambial/${hedge.id}`)}
                      >
                        <td className="p-4">
                          <span className="font-medium">{hedge.hedgeCode}</span>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <div>
                            <p className="font-medium">{hedge.invoice?.invoiceNumber || "-"}</p>
                            <p className="text-sm text-muted-foreground">
                              {hedge.invoice?.supplierName || "-"}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-semibold">{formatUsdc(hedge.hedgeAmountUsdc)}</span>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          <span className="font-semibold text-green-600">
                            R$ {parseFloat(hedge.lockedExchangeRate || "0").toFixed(4)}
                          </span>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="font-semibold">{formatBrl(hedge.lockedAmountBrl)}</span>
                        </td>
                        <td className="p-4">
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-sm",
                              isOverdue && "text-red-600",
                              isNearDue && "text-orange-600"
                            )}>
                              {formatDate(hedge.dueDate)}
                            </span>
                            {isNearDue && (
                              <Clock className="w-4 h-4 text-orange-500" />
                            )}
                            {isOverdue && (
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/protecao-cambial/${hedge.id}`);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Detalhes
                              </DropdownMenuItem>
                              {hedge.status === "PENDING_GUARANTEE" && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/protecao-cambial/${hedge.id}/garantia`);
                                  }}
                                >
                                  <Wallet className="w-4 h-4 mr-2" />
                                  Depositar Garantia
                                </DropdownMenuItem>
                              )}
                              {(hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT") && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/protecao-cambial/${hedge.id}/liquidar`);
                                  }}
                                >
                                  <TrendingUp className="w-4 h-4 mr-2" />
                                  Liquidar Hedge
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page} de {totalPages} ({total} hedges)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Modal do Simulador */}
      <HedgeSimulatorModal
        open={showSimulator}
        onClose={() => setShowSimulator(false)}
      />
    </div>
  );
}
